import React, { useEffect, useMemo, useState } from 'react';
import { logger } from '../../utils/logger';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, DownloadIcon, FileSpreadsheetIcon, BriefcaseIcon, LockIcon, TrendingUpIcon, DollarSignIcon, ActivityIcon, UserIcon, UsersIcon, BarChart3Icon, XIcon, CalendarIcon, FilterIcon, ListIcon, LayoutGridIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, PieChartIcon, ClockIcon, FileTextIcon, TagIcon } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, AreaChart, Area } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Select } from '../ui/Select';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Service } from '../../types';
import { formatCurrency, getToday, getDoctorPrefix } from '../../utils/helpers';
import { exportServicesToExcel } from '../../utils/excelExport';
import { exportServicesToPdf, exportServiceDetailToPdf, exportDashboardChartDataToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useConfirm } from '../../contexts/ConfirmContext';
import { Pagination } from '../ui/Pagination';
import { hasPermission } from '../../types';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
const COMPARE_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#14b8a6', '#6366f1'];
export function Services({
  autoOpenNew = false
}: {
  autoOpenNew?: boolean;
} = {}) {
  const {
    services,
    addService,
    updateService,
    deleteService,
    doctors,
    appointments,
    patients,
    getPatient,
    getDoctor,
    getSpecialty,
    getActiveOfferForService
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('services');
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const {
    confirm
  } = useConfirm();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canViewStats = hasPermission(user?.role || '', 'services_stats', 'read');
  const canDownloadStatsPdf = isAdmin || user?.role === 'supervisor';
  // Tab State
  const [activeTab, setActiveTab] = useState('gestion');
  // Management State
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Patients by Service Modal
  const [isPatientsModalOpen, setIsPatientsModalOpen] = useState(false);
  const [selectedServiceForPatients, setSelectedServiceForPatients] = useState<Service | null>(null);
  const [patientsModalSearch, setPatientsModalSearch] = useState('');
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-services-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  // Load deactivated/archived statuses for doctors (to filter from assignment)
  const [doctorStatuses, setDoctorStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem('savidna-doctors-status');
      if (saved) setDoctorStatuses(JSON.parse(saved));
    } catch {}
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'savidna-doctors-status' && e.newValue) {
        try {
          setDoctorStatuses(JSON.parse(e.newValue));
        } catch {}
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  const isDoctorActive = (id: string) => !doctorStatuses[id];
  const activeDoctors = doctors.filter(d => isDoctorActive(d.id));
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateService(id, {
      status: 'archived'
    } as any).catch(e => logger.error('Error archiving service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateService(id, {
      status: 'deactivated'
    } as any).catch(e => logger.error('Error deactivating service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateService(id, {
      status: 'active'
    } as any).catch(e => logger.error('Error restoring service in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  // Analytics State
  const [compareServiceIds, setCompareServiceIds] = useState<string[]>([]);
  const [compareSearchTerm, setCompareSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: '2020-01-01',
    end: new Date().toISOString().split('T')[0]
  });
  const [groupBy, setGroupBy] = useState('month'); // 'day', 'week', 'month'
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    duration: 30,
    price: '',
    cost: '',
    description: '',
    doctorIds: [] as string[]
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Filtered services for Management
  const filteredServices = services.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(s.id);
    return matchesSearch && status === viewMode;
  });
  const {
    paginatedItems: paginatedServices,
    currentPage: servicesPage,
    totalPages: servicesTotalPages,
    totalItems: servicesTotalItems,
    startIndex: servicesStartIndex,
    endIndex: servicesEndIndex,
    goToPage: servicesGoToPage,
    nextPage: servicesNextPage,
    prevPage: servicesPrevPage
  } = usePagination(filteredServices);
  // Counts for tabs
  const activeCount = services.filter(s => getItemStatus(s.id) === 'active').length;
  const archivedCount = services.filter(s => getItemStatus(s.id) === 'archived').length;
  const deactivatedCount = services.filter(s => getItemStatus(s.id) === 'deactivated').length;
  // Filtered services for Analytics Selector
  const filteredCompareServices = services.filter(s => s.name.toLowerCase().includes(compareSearchTerm.toLowerCase()));
  // --- ANALYTICS CALCULATIONS ---
  // 1. Filter appointments based on date range and status
  const filteredAppointments = useMemo(() => {
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    end.setHours(23, 59, 59, 999); // End of the day
    return appointments.filter(apt => {
      const aptDate = new Date(apt.date);
      return apt.status === 'completada' && aptDate >= start && aptDate <= end;
    });
  }, [appointments, dateRange]);
  // 2. Aggregate data based on Group By and Selected Services
  const chartData = useMemo(() => {
    const dataMap: Record<string, any> = {};
    // Helper to generate keys based on group by
    const getKey = (date: Date) => {
      if (groupBy === 'day') return date.toISOString().split('T')[0];
      if (groupBy === 'week') {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        d.setDate(d.getDate() + 4 - (d.getDay() || 7));
        const yearStart = new Date(d.getFullYear(), 0, 1);
        const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
        return `${d.getFullYear()}-W${weekNo}`;
      }
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // month
    };
    // Initialize map with 0s for selected services if needed, or just build dynamically
    filteredAppointments.forEach(apt => {
      const allServiceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      // If comparing specific services, check if any of the appointment's services match
      if (compareServiceIds.length > 0 && !allServiceIds.some(sId => compareServiceIds.includes(sId))) return;
      const date = new Date(apt.date);
      const key = getKey(date);
      if (!dataMap[key]) {
        dataMap[key] = {
          name: key,
          totalCount: 0,
          totalRevenue: 0
        };
      }
      allServiceIds.forEach(sId => {
        if (compareServiceIds.length > 0 && !compareServiceIds.includes(sId)) return;
        const service = services.find(s => s.id === sId);
        if (service) {
          const discountPercent = Number(apt.serviceDiscounts?.[sId] || 0);
          const netPrice = Math.max(0, service.price - service.price * (discountPercent / 100));
          dataMap[key][service.name] = (dataMap[key][service.name] || 0) + 1;
          dataMap[key][`${service.name}_revenue`] = (dataMap[key][`${service.name}_revenue`] || 0) + netPrice;
          dataMap[key].totalCount += 1;
          dataMap[key].totalRevenue += netPrice;
        }
      });
    });
    // Sort by date key
    return Object.values(dataMap).sort((a: any, b: any) => a.name.localeCompare(b.name));
  }, [filteredAppointments, groupBy, compareServiceIds, services]);
  // 3. Summary Table Data
  const summaryData = useMemo(() => {
    const summary: Record<string, {
      count: number;
      revenue: number;
      cost: number;
      name: string;
    }> = {};
    // Initialize for selected services to ensure they appear even if 0
    if (compareServiceIds.length > 0) {
      compareServiceIds.forEach(id => {
        const s = services.find(ser => ser.id === id);
        if (s) summary[id] = {
          count: 0,
          revenue: 0,
          cost: s.cost || 0,
          name: s.name
        };
      });
    } else {
      // If none selected, initialize all
      services.forEach(s => {
        summary[s.id] = {
          count: 0,
          revenue: 0,
          cost: s.cost || 0,
          name: s.name
        };
      });
    }
    filteredAppointments.forEach(apt => {
      const allServiceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      if (compareServiceIds.length > 0 && !allServiceIds.some(sId => compareServiceIds.includes(sId))) return;
      allServiceIds.forEach(sId => {
        if (compareServiceIds.length > 0 && !compareServiceIds.includes(sId)) return;
        const service = services.find(s => s.id === sId);
        if (service) {
          if (!summary[service.id]) summary[service.id] = {
            count: 0,
            revenue: 0,
            cost: (service as any).cost || 0,
            name: service.name
          };
          summary[service.id].count += 1;
          const discountPercent = Number(apt.serviceDiscounts?.[sId] || 0);
          summary[service.id].revenue += Math.max(0, service.price - service.price * (discountPercent / 100));
        }
      });
    });
    return Object.values(summary).sort((a, b) => b.revenue - a.revenue);
  }, [filteredAppointments, compareServiceIds, services]);
  // 4. KPI Calculations
  const kpiData = useMemo(() => {
    const totalRevenue = summaryData.reduce((sum, item) => sum + item.revenue, 0);
    const totalCount = summaryData.reduce((sum, item) => sum + item.count, 0);
    const topService = summaryData.length > 0 ? summaryData[0] : null;
    const avgTicket = totalCount > 0 ? totalRevenue / totalCount : 0;
    return {
      totalRevenue,
      totalCount,
      topService,
      avgTicket
    };
  }, [summaryData]);
  // Pagination for summary table in statistics tab
  const {
    paginatedItems: paginatedSummary,
    currentPage: summaryPage,
    totalPages: summaryTotalPages,
    totalItems: summaryTotalItems,
    startIndex: summaryStartIndex,
    endIndex: summaryEndIndex,
    goToPage: summaryGoToPage,
    nextPage: summaryNextPage,
    prevPage: summaryPrevPage
  } = usePagination(summaryData, {
    itemsPerPage: 10
  });
  // --- HANDLERS ---
  const openPatientsModal = (service: Service) => {
    setSelectedServiceForPatients(service);
    setIsPatientsModalOpen(true);
  };
  const servicePatients = useMemo(() => {
    if (!selectedServiceForPatients) return [];
    const serviceId = selectedServiceForPatients.id;
    const completedApts = appointments.filter(a => {
      if (a.status !== 'completada') return false;
      const allServiceIds = a.serviceIds && a.serviceIds.length > 0 ? a.serviceIds : [a.serviceId].filter(Boolean);
      return allServiceIds.includes(serviceId);
    });
    const patientMap: Record<string, {
      patient: any;
      count: number;
      lastDate: string;
    }> = {};
    completedApts.forEach(apt => {
      if (!patientMap[apt.patientId]) {
        const p = getPatient(apt.patientId);
        if (p) {
          patientMap[apt.patientId] = {
            patient: p,
            count: 0,
            lastDate: apt.date
          };
        }
      }
      if (patientMap[apt.patientId]) {
        patientMap[apt.patientId].count += 1;
        if (apt.date > patientMap[apt.patientId].lastDate) {
          patientMap[apt.patientId].lastDate = apt.date;
        }
      }
    });
    return Object.values(patientMap).sort((a, b) => b.lastDate.localeCompare(a.lastDate));
  }, [selectedServiceForPatients, appointments, getPatient]);
  const filteredServicePatients = useMemo(() => {
    if (!patientsModalSearch) return servicePatients;
    const q = patientsModalSearch.toLowerCase();
    return servicePatients.filter(item => {
      const name = item.patient.name?.toLowerCase() || '';
      const phone = item.patient.phone?.toLowerCase() || '';
      const email = item.patient.email?.toLowerCase() || '';
      return name.includes(q) || phone.includes(q) || email.includes(q);
    });
  }, [servicePatients, patientsModalSearch]);
  const openNewForm = () => {
    if (!canCreate('services')) return;
    setSelectedService(null);
    setFormData({
      name: '',
      sku: '',
      duration: 30,
      price: '',
      cost: '',
      description: '',
      doctorIds: []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  // Auto-open new form when navigated with :new param
  useEffect(() => {
    if (autoOpenNew) {
      const t = setTimeout(() => openNewForm(), 100);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenNew]);
  const openEditForm = (service: Service) => {
    if (!canEdit('services')) return;
    setSelectedService(service);
    setFormData({
      name: service.name,
      sku: service.sku || '',
      duration: service.duration,
      price: String(service.price),
      cost: service.cost ? String(service.cost) : '',
      description: service.description,
      doctorIds: service.doctorIds || []
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openActionConfirm = (service: Service, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedService(service);
    let title = '';
    let message = '';
    let confirmText = '';
    let variant: 'danger' | 'warning' | 'info' | 'primary' = 'primary';
    if (type === 'archive') {
      title = 'Archivar Servicio';
      message = `¿Estás seguro de que deseas archivar "${service.name}"? Podrás restaurarlo más tarde.`;
      confirmText = 'Archivar';
      variant = 'warning';
    } else if (type === 'deactivate') {
      title = 'Desactivar Servicio';
      message = `¿Estás seguro de que deseas desactivar "${service.name}"? No aparecerá en las listas activas.`;
      confirmText = 'Desactivar';
      variant = 'warning';
    } else if (type === 'delete') {
      title = 'Eliminar Servicio';
      message = `¿Estás seguro de que deseas eliminar permanentemente "${service.name}"? Esta acción no se puede deshacer.`;
      confirmText = 'Eliminar';
      variant = 'danger';
    }
    confirm({
      title,
      message,
      confirmText,
      variant,
      onConfirm: () => {
        if (type === 'archive') {
          archiveItem(service.id);
          showToast({
            type: 'info',
            title: 'Servicio archivado',
            message: `${service.name} fue archivado.`
          });
        } else if (type === 'deactivate') {
          deactivateItem(service.id);
          showToast({
            type: 'warning',
            title: 'Servicio desactivado',
            message: `${service.name} fue desactivado.`
          });
        } else if (type === 'delete') {
          deleteService(service.id);
          showToast({
            type: 'error',
            title: 'Servicio eliminado',
            message: `${service.name} fue eliminado permanentemente.`
          });
        }
      }
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (formData.duration <= 0) errors.duration = 'La duración debe ser mayor a 0';
    const priceNum = parseInt(formData.price, 10);
    if (isNaN(priceNum) || priceNum < 0) errors.price = 'El precio no puede ser negativo';
    const costNum = formData.cost === '' ? 0 : parseInt(formData.cost, 10);
    if (isNaN(costNum) || costNum < 0) errors.cost = 'El costo no puede ser negativo';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    const dataToSave = {
      name: formData.name,
      sku: formData.sku || undefined,
      duration: formData.duration,
      price: priceNum,
      cost: costNum,
      description: formData.description,
      doctorIds: formData.doctorIds
    };
    confirm({
      title: selectedService ? 'Guardar Cambios' : 'Crear Servicio',
      message: selectedService ? '¿Estás seguro de que deseas guardar los cambios de este servicio?' : '¿Estás seguro de que deseas registrar este nuevo servicio?',
      confirmText: 'Guardar',
      onConfirm: async () => {
        try {
          if (selectedService) {
            await updateService(selectedService.id, dataToSave);
            showToast({
              type: 'success',
              title: 'Servicio actualizado',
              message: `${formData.name} fue actualizado correctamente.`
            });
          } else {
            await addService(dataToSave);
            showToast({
              type: 'success',
              title: 'Servicio creado',
              message: `${formData.name} fue registrado correctamente.`
            });
          }
          setIsFormOpen(false);
        } catch (error) {
          logger.error('Error al guardar:', error);
          showToast({
            type: 'error',
            title: 'Error al guardar',
            message: 'No se pudo guardar. Revisa tu internet o si el SKU ya existe.'
          });
        }
      }
    });
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToExcel(services);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportServicesToPdf(services);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  const toggleDoctorSelection = (doctorId: string) => {
    setFormData(prev => {
      const currentIds = prev.doctorIds || [];
      return currentIds.includes(doctorId) ? {
        ...prev,
        doctorIds: currentIds.filter(id => id !== doctorId)
      } : {
        ...prev,
        doctorIds: [...currentIds, doctorId]
      };
    });
  };
  const toggleCompareService = (serviceId: string) => {
    setCompareServiceIds(prev => prev.includes(serviceId) ? prev.filter(id => id !== serviceId) : [...prev, serviceId]);
  };
  const tabs = [{
    id: 'gestion',
    label: 'Gestión de Servicios',
    icon: <LayoutGridIcon className="w-4 h-4" data-id="element-5494" data-mp="3vk0w4yfcm3q2" />
  }, ...(canViewStats ? [{
    id: 'estadisticas',
    label: 'Estadísticas y Reportes',
    icon: <BarChart3Icon className="w-4 h-4" data-id="element-5495" data-mp="d4gmal870jai" />
  }] : [])];
  return <div className="space-y-6" data-id="element-5496" data-mp="876oah6qmzb2">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl p-6 text-white shadow-xl" style={{
      background: 'linear-gradient(135deg,#0c4a6e,#0284c7,#38bdf8)'
    }} data-id="element-5497" data-mp="n3630aocrxp6">
        
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 13px, rgba(255,255,255,1) 13px, rgba(255,255,255,1) 14px)'
      }} data-id="element-5498" data-mp="11rfvhxp9tlrb" />
        
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-5499" data-mp="wpgq6i9z0erq" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-5500" data-mp="mtdpzhyoiq7f" />
        <div className="absolute inset-0 pointer-events-none opacity-10" style={{
        background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 40%, transparent 60%, rgba(255,255,255,0.15) 100%)'
      }} data-id="element-5501" data-mp="91pengw985ri" />
        
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-5502" data-mp="3vaql7iz0q4o5" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-5503" data-mp="44guxbvgjto5">
          <div data-id="element-5504" data-mp="12e6y1av7uucp">
            <div className="flex items-center gap-3 mb-2" data-id="element-5505" data-mp="12dnh9xhayc09">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm" data-id="element-5506" data-mp="12g9xsmm681um">
                <BriefcaseIcon className="w-6 h-6 text-white" data-id="element-5507" data-mp="xldtamzqzm0m" />
              </div>
              <h1 className="text-2xl font-bold text-white" data-id="element-5508" data-mp="so3w2l64a628">Servicios</h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-5509" data-mp="nqubbitoaikq">
              Administración y análisis de servicios médicos
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-5510" data-mp="eo2wd91l94oc">
            {canExportPdf && <Button variant="outline" onClick={() => setIsPdfExportModalOpen(true)} isLoading={isPdfExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" leftIcon={<BriefcaseIcon className="w-4 h-4" data-id="element-5512" data-mp="244c24wkrbzin" />} data-id="element-5511" data-mp="9phxbcy2or1h">
              
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-5514" data-mp="2qdskmexds4vq" />} data-id="element-5513" data-mp="2gld31ipzxibd">
              
                Exportar Excel
              </Button>}
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-5515" data-mp="1xniyphztbuzb">
        {/* ==================== TAB 1: GESTIÓN ==================== */}
        <TabPanel id="gestion" activeTab={activeTab} data-id="element-5516" data-mp="27l6wkgsptilp">
          <div className="space-y-6" data-id="element-5517" data-mp="27mrodynnanfv">
            {/* Actions Bar */}
            <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-5518" data-mp="3e0h95jgwavlb">
              <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto" data-id="element-5519" data-mp="3izu46rhx78yz">
                <div className="w-full sm:w-64 relative" data-id="element-5520" data-mp="1iy44byee8qji">
                  <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-5521" data-mp="1iwj9e7o635l4" />
                  <Input style={{
                  paddingLeft: '2.5rem'
                }} placeholder="Buscar servicios..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="" data-id="element-5522" data-mp="1rz2587tjchxv" />
                  
                </div>

                {canCreate('services') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-5524" data-mp="19181a879kwmc" />} onClick={openNewForm} data-id="element-5523" data-mp="1906ej6gvylos">
                  
                    Nuevo Servicio
                  </Button>}
              </div>

              {/* View Mode Tabs */}
              <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-5525" data-mp="ghiy44cu3uu1">
                {[{
                key: 'active',
                label: 'Activos',
                count: activeCount,
                icon: CheckCircleIcon
              }, {
                key: 'archived',
                label: 'Archivados',
                count: archivedCount,
                icon: ArchiveIcon
              }, {
                key: 'deactivated',
                label: 'Desactivados',
                count: deactivatedCount,
                icon: XCircleIcon
              }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-5526" data-mp="b5nai82m4irn">
                  
                    <tab.icon className="w-4 h-4" data-id="element-5527" data-mp="2i3df1bw2lz1a" />
                    {tab.label}
                    <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-5528" data-mp="2d5240xu1hihh">
                      {tab.count}
                    </span>
                  </button>)}
              </div>
            </div>

            {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-5529" data-mp="30uwfpc2mh105">
                <LockIcon className="w-4 h-4" data-id="element-5530" data-mp="2r5cy09qnbd6v" />
                <span data-id="element-5531" data-mp="2r41u89x7a5fr">
                  Solo tienes permisos de lectura. No puedes crear, editar o
                  eliminar servicios.
                </span>
              </div>}

            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5532" data-mp="3evz25pxj4dbl">
              {paginatedServices.length === 0 ? <div className="col-span-full text-center py-16 text-gray-500 dark:text-gray-400" data-id="element-5533" data-mp="11e4cfekq9ckv">
                  <div className="flex flex-col items-center justify-center" data-id="element-5534" data-mp="16cyvmhmlx35i">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-5535" data-mp="1adhqkkjsqpbx">
                      <BriefcaseIcon className="w-8 h-8 text-gray-400" data-id="element-5536" data-mp="1fef76sp5rbbb" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-5537" data-mp="1kb5il2q0piqe">
                      No se encontraron servicios
                    </h3>
                    <p className="text-sm" data-id="element-5538" data-mp="1u18f3cwcleth">
                      {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando un nuevo servicio' : 'No hay servicios en esta vista'}
                    </p>
                  </div>
                </div> : paginatedServices.map(service => {
              const activeOffer = getActiveOfferForService(service.id);
              const discountedPrice = activeOffer ? Math.max(0, service.price - service.price * (activeOffer.discountPercent / 100)) : null;
              return <Card key={service.id} className="flex flex-col group overflow-hidden transition-all duration-200 hover:shadow-xl hover:-translate-y-1 border-0 shadow-sm ring-1 ring-gray-200/80 dark:ring-gray-700/60 p-0" data-id="element-5539" data-mp="34cwvmwqx8cg2">
                    
                      {/* Card header with colored top band */}
                      <div className="relative px-5 pt-4 pb-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800/40" data-id="element-5540" data-mp="3n8n2oydpnz0t">
                        {/* Top accent line */}

                        <div className="flex items-start justify-between gap-2" data-id="element-5541" data-mp="jlwfcqk8vuzf">
                          <div className="flex-1 min-w-0" data-id="element-5542" data-mp="sz52kf6yue0a">
                            <h3 className="font-bold text-gray-900 dark:text-white text-base leading-snug truncate" data-id="element-5543" data-mp="sxadcoonvzqe">
                              {service.name}
                            </h3>
                            <div className="flex items-center gap-2 mt-2 flex-wrap" data-id="element-5544" data-mp="12rkfgy3eoceb">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-blue-900/30 border text-xs font-medium" style={{
                          borderColor: '#1a9fd4',
                          color: '#1a9fd4'
                        }} data-id="element-5545" data-mp="xr558fef7l0k">
                              
                                <ClockIcon className="w-3 h-3" data-id="element-5546" data-mp="eykhtdhhq845" />
                                {service.duration} min
                              </span>
                              {activeOffer && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 text-xs font-semibold text-emerald-700 dark:text-emerald-400" data-id="element-5547" data-mp="1qesnvow4s1kk">
                                  <TagIcon className="w-3 h-3" data-id="element-5548" data-mp="27g55gnhpp9u3" />-
                                  {activeOffer.discountPercent}% Oferta
                                </span>}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right" data-id="element-5549" data-mp="1jqu0sux8qvao">
                            {activeOffer ? <>
                                <p className="text-xs text-gray-400 line-through leading-none" data-id="element-5550" data-mp="1oovsu5cna4ya">
                                  {formatCurrency(service.price)}
                                </p>
                                <p className="text-xl font-extrabold leading-none text-emerald-600 dark:text-emerald-400 mt-0.5" data-id="element-5551" data-mp="w087yavt5hl9">
                                  {formatCurrency(discountedPrice || 0)}
                                </p>
                              </> : <p className="text-xl font-extrabold leading-none" style={{
                        color: '#1a9fd4'
                      }} data-id="element-5552" data-mp="oi0aeoc569ez">
                            
                                {formatCurrency(service.price)}
                              </p>}
                            {service.sku && <p className="text-[10px] text-gray-400 font-mono mt-1" data-id="element-5553" data-mp="5qzde5ul0ny2">
                                {service.sku}
                              </p>}
                          </div>
                        </div>
                        {/* Action buttons */}
                      </div>
                      {/* Card body */}
                      <div className="flex flex-col flex-1 px-5 py-3 gap-2" data-id="element-5554" data-mp="3ixr7vdmfilf7">
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2" data-id="element-5555" data-mp="3izvlina4qyso">
                          {service.description || <span className="italic text-gray-400 dark:text-gray-600" data-id="element-5556" data-mp="gido2eka20eu">
                              Sin descripción
                            </span>}
                        </p>
                        {/* Doctors */}
                        <div className="pt-2 border-t border-gray-100 dark:border-gray-700/60" data-id="element-5557" data-mp="6py63h8zdbty">
                          <p className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5" data-id="element-5558" data-mp="3p00ag6heds2u">
                            <UserIcon className="w-3 h-3" data-id="element-5559" data-mp="3fyam59itb99a" /> Doctores
                          </p>
                          <div className="flex flex-wrap gap-1.5" data-id="element-5560" data-mp="3jzvt6e8o0tbk">
                            {service.doctorIds && service.doctorIds.length > 0 ? service.doctorIds.map(docId => {
                        const doctor = getDoctor(docId);
                        return doctor ? <span key={docId} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white dark:bg-blue-900/30 border text-xs font-medium" style={{
                          borderColor: '#1a9fd4',
                          color: '#1a9fd4'
                        }} data-id="element-5561" data-mp="32nfl1izxccve">
                              
                                    <span className="w-4 h-4 rounded-full text-white flex items-center justify-center text-[9px] font-bold flex-shrink-0" style={{
                            background: '#1a9fd4'
                          }} data-id="element-5562" data-mp="3hf7l6nr5dkcj">
                                
                                      {doctor.name.charAt(0)}
                                    </span>
                                    {getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)}{' '}
                                    {doctor.name}
                                  </span> : null;
                      }) : <span className="text-xs text-gray-400 italic" data-id="element-5563" data-mp="dyhnbjsb7ztu">
                                Sin doctor asignado
                              </span>}
                          </div>
                        </div>
                        {/* Action buttons at the very bottom border */}
                        <div className="flex justify-end gap-1 pt-2 border-t border-gray-100 dark:border-gray-700/60 mt-1 opacity-0 group-hover:opacity-100 transition-all duration-150" data-id="element-5564" data-mp="sne0twd4y51u">
                          <button onClick={() => openPatientsModal(service)} className="p-1.5 text-gray-400 hover:text-teal-600 rounded-lg transition-colors" title="Ver Pacientes Atendidos" data-id="element-5565" data-mp="11ovihj66yesn">
                          
                            <UsersIcon className="w-4 h-4" data-id="element-5566" data-mp="2m3ry407adplb" />
                          </button>
                          {!readOnly && viewMode === 'active' ? <>
                              {canEdit('services') && <button onClick={() => openEditForm(service)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg transition-colors" title="Editar" data-id="element-5567" data-mp="39uw7h2it8ea3">
                            
                                  <EditIcon className="w-4 h-4" data-id="element-5568" data-mp="31097ilvx0u58" />
                                </button>}
                              {canDelete('services') && <>
                                  <button onClick={() => openActionConfirm(service, 'archive')} className="p-1.5 text-gray-400 hover:text-amber-500 rounded-lg transition-colors" title="Archivar" data-id="element-5569" data-mp="3osg6ztwxth8v">
                              
                                    <ArchiveIcon className="w-4 h-4" data-id="element-5570" data-mp="2n4l27bmga38x" />
                                  </button>
                                  <button onClick={() => openActionConfirm(service, 'deactivate')} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg transition-colors" title="Desactivar" data-id="element-5571" data-mp="3fvcs5rcx9vnt">
                              
                                    <PowerOffIcon className="w-4 h-4" data-id="element-5572" data-mp="3axuu4w0g10ul" />
                                  </button>
                                </>}
                            </> : !readOnly && viewMode !== 'active' ? <button onClick={() => restoreItem(service.id)} className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg transition-colors" title="Restaurar" data-id="element-5573" data-mp="1rw108o6qzggt">
                          
                              <RotateCcwIcon className="w-4 h-4" data-id="element-5574" data-mp="1wxgi67dmp6uz" />
                            </button> : null}
                        </div>
                      </div>
                    </Card>;
            })}
            </div>

            {/* Pagination */}
            <div className="pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-5575" data-mp="1iww23iz3lp56">
              <Pagination currentPage={servicesPage} totalPages={servicesTotalPages} totalItems={servicesTotalItems} startIndex={servicesStartIndex} endIndex={servicesEndIndex} onPageChange={servicesGoToPage} onNext={servicesNextPage} onPrev={servicesPrevPage} data-id="element-5576" data-mp="1e5dy0m1s38fz" />
              
            </div>
          </div>
        </TabPanel>

        {/* ==================== TAB 2: ESTADÍSTICAS ==================== */}
        <TabPanel id="estadisticas" activeTab={activeTab} data-id="element-5577" data-mp="3rv0vjx6cmbr1">
          <div className="space-y-6" data-id="element-5578" data-mp="3mxika04uzkfv">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-5579" data-mp="3mv651uqhjo6i">
              <Card className="bg-gradient-to-br from-blue-900 to-blue-600 text-white border-none" data-id="element-5580" data-mp="2z7xy78des72d">
                <div className="flex items-center justify-between mb-2" data-id="element-5581" data-mp="2uaei02x5hk7s">
                  <p className="text-blue-200 text-sm font-medium" data-id="element-5582" data-mp="38y9dfr2jm2ok">
                    Ingresos Totales
                  </p>
                  <DollarSignIcon className="w-5 h-5 text-blue-200" data-id="element-5583" data-mp="345g5kubs2e89" />
                </div>
                <p className="text-2xl font-bold" data-id="element-5584" data-mp="2gfc5gwxsswm0">
                  {formatCurrency(kpiData.totalRevenue)}
                </p>
                <p className="text-xs text-blue-200 mt-1" data-id="element-5585" data-mp="2bg97txk5dso2">
                  En el periodo seleccionado
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-emerald-500" data-id="element-5586" data-mp="vrt7l66fodke">
                <div className="flex items-center justify-between mb-2" data-id="element-5587" data-mp="i0634j2c7yqj">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-5588" data-mp="m0oukc0bj2s7">
                    Citas Completadas
                  </p>
                  <CheckCircleIcon className="w-5 h-5 text-emerald-500" data-id="element-5589" data-mp="19r2dnd0xdran" />
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5590" data-mp="1eocdznpurnd9">
                  {kpiData.totalCount}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-5591" data-mp="10s7a68p9mvb1">
                  Servicios realizados
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-blue-500" data-id="element-5592" data-mp="3lfafwhnfjtje">
                <div className="flex items-center justify-between mb-2" data-id="element-5593" data-mp="3qg7ruchujrcg">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-5594" data-mp="1vzhkopsmf85h">
                    Ticket Promedio
                  </p>
                  <ActivityIcon className="w-5 h-5 text-blue-500" data-id="element-5595" data-mp="1r0emnpb26270" />
                </div>
                <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5596" data-mp="1m7tvkvjje376">
                  {formatCurrency(kpiData.avgTicket)}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-5597" data-mp="1d86xaim3tdic">
                  Por servicio realizado
                </p>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-l-4 border-l-amber-500" data-id="element-5598" data-mp="1882eglxq46oy">
                <div className="flex items-center justify-between mb-2" data-id="element-5599" data-mp="13aspf7e8xx1n">
                  <p className="text-gray-500 dark:text-gray-400 text-sm font-medium" data-id="element-5600" data-mp="z9qqkro8rn07">
                    Top Servicio
                  </p>
                  <TrendingUpIcon className="w-5 h-5 text-amber-500" data-id="element-5601" data-mp="2xm7h3tqti6k1" />
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white truncate" title={kpiData.topService?.name || '-'} data-id="element-5602" data-mp="2tnhtl8c2f8mo">
                  
                  {kpiData.topService?.name || '-'}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-5603" data-mp="p4igaqswie7p">
                  {kpiData.topService ? formatCurrency(kpiData.topService.revenue) : '$0'}{' '}
                  generados
                </p>
              </Card>
            </div>

            {/* 1. FILTERS SECTION */}
            <Card data-id="element-5604" data-mp="1ffztma06res">
              <CardHeader className="border-b border-gray-100 dark:border-gray-700 pb-4" data-id="element-5605" data-mp="1h0v5e456edp">
                <CardTitle className="flex items-center gap-2 text-base" data-id="element-5606" data-mp="5ivu4bylofob">
                  <FilterIcon className="w-4 h-4" data-id="element-5607" data-mp="agnordqmqfw7" /> Configuración del Análisis
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6" data-id="element-5608" data-mp="1gyapjbb5uzhb">
                <div className="grid grid-cols-1 gap-4" data-id="element-5609" data-mp="1lu84bd6rzxrz">
                  {/* Date Range */}
                  <div className="space-y-2" data-id="element-5610" data-mp="1kd2j1ggfh50v">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5611" data-mp="1kfyft1gjhnnq">
                      Rango de Fecha
                    </label>
                    <div className="flex items-center gap-2" data-id="element-5612" data-mp="1tflhlod6kvhb">
                      <Input type="date" value={dateRange.start} onChange={e => setDateRange(prev => ({
                      ...prev,
                      start: e.target.value
                    }))} max={dateRange.end || getToday()} className="bg-gray-50 dark:bg-gray-900" data-id="element-5613" data-mp="1pcymnel7qufw" />
                      

                      <span className="text-gray-400" data-id="element-5614" data-mp="285stfl8ong91">-</span>
                      <Input type="date" value={dateRange.end} onChange={e => setDateRange(prev => ({
                      ...prev,
                      end: e.target.value
                    }))} max={getToday()} className="bg-gray-50 dark:bg-gray-900" data-id="element-5615" data-mp="2m3zeu1wg3nrt" />
                      
                    </div>
                  </div>

                  {/* Group By */}
                  <div className="space-y-2" data-id="element-5616" data-mp="2hzovzldpjpdw">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5617" data-mp="2hynaso2z9mig">
                      Agrupar Por
                    </label>
                    <Select value={groupBy} onChange={setGroupBy} options={[{
                    value: 'day',
                    label: 'Día'
                  }, {
                    value: 'week',
                    label: 'Semana'
                  }, {
                    value: 'month',
                    label: 'Mes'
                  }]} data-id="element-5618" data-mp="281iwtbjacxcq" />
                    
                  </div>

                  {/* Service Selector */}
                  <div className="space-y-2" data-id="element-5619" data-mp="3vzteudk6kw3l">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-5620" data-mp="3vwns34z0y6b1">
                      Comparar Servicios
                    </label>
                    <div className="relative" data-id="element-5621" data-mp="shrp2perd0p5">
                      <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-5622" data-mp="njge2bcq8k5c" />
                      <input type="text" placeholder="Buscar servicios..." value={compareSearchTerm} onChange={e => setCompareSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" data-id="element-5623" data-mp="jgk396tfhsby" />
                      
                    </div>

                    {/* Selected Tags */}
                    {compareServiceIds.length > 0 && <div className="flex flex-wrap gap-2 mt-2" data-id="element-5624" data-mp="1npk4ssmr723n">
                        {compareServiceIds.map((id, index) => {
                      const svc = services.find(s => s.id === id);
                      if (!svc) return null;
                      return <span key={id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium text-white shadow-sm" style={{
                        backgroundColor: COMPARE_COLORS[index % COMPARE_COLORS.length]
                      }} data-id="element-5625" data-mp="22egf6wca8r7f" data-mpi={index}>
                            
                              {svc.name}
                              <button onClick={() => toggleCompareService(id)} className="hover:opacity-75" data-id="element-5626" data-mp="3ub29hyl8s8j9">
                              
                                <XIcon className="w-3 h-3" data-id="element-5627" data-mp="6xykmj9ke7ap" />
                              </button>
                            </span>;
                    })}
                        <button onClick={() => setCompareServiceIds([])} className="text-xs text-gray-500 hover:text-red-500 px-1 font-medium" data-id="element-5628" data-mp="3bd83zujbof5m">
                        
                          Limpiar
                        </button>
                      </div>}

                    {/* Dropdown list */}
                    <div className="max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg mt-2 p-1 bg-white dark:bg-gray-800 shadow-sm" data-id="element-5629" data-mp="3fgxni5l546js">
                      {filteredCompareServices.map(service => <label key={service.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded cursor-pointer transition-colors" data-id="element-5630" data-mp="135urvxxqt9u4">
                        
                          <input type="checkbox" checked={compareServiceIds.includes(service.id)} onChange={() => toggleCompareService(service.id)} className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500" data-id="element-5631" data-mp="t8qccgyekcma" />
                        

                          <span className="text-sm text-gray-700 dark:text-gray-300 truncate" data-id="element-5632" data-mp="1c6ko32qx128d">
                            {service.name}
                          </span>
                        </label>)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 2. CHARTS SECTION */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5633" data-mp="29ni02gklsvp9">
              {/* Quantity Chart */}
              <Card data-id="element-5634" data-mp="3gkshwfbpo5nv">
                <CardHeader data-id="element-5635" data-mp="3gk8xmryl956o">
                  <div className="flex items-center justify-between" data-id="element-5636" data-mp="3gjhcyc6j4rv6">
                    <CardTitle className="flex items-center gap-2" data-id="element-5637" data-mp="3lb8wi2r9pxbt">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-5638" data-mp="3qalunjnlaqtp">
                        <BarChart3Icon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-5639" data-mp="3qcol24snmlyy" />
                      </div>
                      Tendencia de Servicios
                    </CardTitle>
                    {canDownloadStatsPdf && chartData.length > 0 && <button onClick={() => {
                    const cols = [{
                      header: 'Periodo',
                      key: 'periodo'
                    }, {
                      header: 'Total Servicios',
                      key: 'total'
                    }];
                    const rows = chartData.map((d: any) => ({
                      label: d.name,
                      values: {
                        periodo: d.name,
                        total: d.totalCount
                      }
                    }));
                    exportDashboardChartDataToPdf(rows, cols, 'Tendencia de Servicios', {
                      from: dateRange.start,
                      to: dateRange.end
                    });
                  }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-5640" data-mp="32k811opcwjx0">
                      
                        <DownloadIcon className="w-4 h-4" data-id="element-5641" data-mp="341wvodragsqj" />
                      </button>}
                  </div>
                </CardHeader>
                <CardContent data-id="element-5642" data-mp="3n4yo8lnk6un7">
                  <div className="h-[220px] sm:h-[300px] w-full" data-id="element-5643" data-mp="3ry1ofcmud2cd">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5644" data-mp="3rzcr194jw41w">
                      <AreaChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }} data-id="element-5645" data-mp="3e7ogha9y9nn1">
                        
                        <defs data-id="element-5646" data-mp="eq6xlhewka7x">
                          <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1" data-id="element-5647" data-mp="r77047txl94">
                            
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} data-id="element-5648" data-mp="2lfuypee8su3q" />
                            

                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-5649" data-mp="2ivgjtkd2g8em" />
                            
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-5650" data-mp="2mzo9bjrclsdw" />
                        

                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-5651" data-mp="1v8gsyyrescpw" />
                        

                        <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} tickLine={false} axisLine={false} data-id="element-5652" data-mp="1gi9e0t0m1bty" />
                        

                        <Tooltip contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }} data-id="element-5653" data-mp="1p4c4pa1fbcde" />
                        

                        <Legend wrapperStyle={{
                        paddingTop: '20px'
                      }} data-id="element-5654" data-mp="27vv4z942ccbq" />
                        

                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Area key={id} type="monotone" dataKey={svc.name} stroke={COMPARE_COLORS[index % COMPARE_COLORS.length]} fill={`url(#color${index})`} // Simplified for demo, ideally unique IDs
                        strokeWidth={2} fillOpacity={0.1} data-id="element-5655" data-mp="sfmlnge28dpu" data-mpi={index} />;
                      }) : <Area type="monotone" dataKey="totalCount" name="Total Servicios" stroke="#3b82f6" fill="url(#colorTotal)" strokeWidth={3} data-id="element-5656" data-mp="3onqnbtcgaklg" />}
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Revenue Chart */}
              <Card data-id="element-5657" data-mp="1z8si7wrfkhe1">
                <CardHeader data-id="element-5658" data-mp="1zcgdp1gfrdve">
                  <div className="flex items-center justify-between" data-id="element-5659" data-mp="20qr7wnt9lnk4">
                    <CardTitle className="flex items-center gap-2" data-id="element-5660" data-mp="20pg5arbk2lul">
                      <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg" data-id="element-5661" data-mp="25pu2tffj6ded">
                        <DollarSignIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-5662" data-mp="1rq2rxnkb9d4l" />
                      </div>
                      Ingresos Generados
                    </CardTitle>
                    {canDownloadStatsPdf && chartData.length > 0 && <button onClick={() => {
                    const cols = [{
                      header: 'Periodo',
                      key: 'periodo'
                    }, {
                      header: 'Ingresos Totales',
                      key: 'ingresos'
                    }];
                    const fmtCurrency = (n: number) => new Intl.NumberFormat('es-DO', {
                      style: 'currency',
                      currency: 'DOP',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(n);
                    const rows = chartData.map((d: any) => ({
                      label: d.name,
                      values: {
                        periodo: d.name,
                        ingresos: fmtCurrency(d.totalRevenue)
                      }
                    }));
                    exportDashboardChartDataToPdf(rows, cols, 'Ingresos Generados por Servicios', {
                      from: dateRange.start,
                      to: dateRange.end
                    });
                  }} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-5663" data-mp="1mrrhpbq3t0lu">
                      
                        <DownloadIcon className="w-4 h-4" data-id="element-5664" data-mp="2tiubb2zp7i9n" />
                      </button>}
                  </div>
                </CardHeader>
                <CardContent data-id="element-5665" data-mp="32geggn0olypj">
                  <div className="h-[220px] sm:h-[300px] w-full" data-id="element-5666" data-mp="32fldlx4uftco">
                    <ResponsiveContainer width="100%" height="100%" data-id="element-5667" data-mp="25sq6isarxch2">
                      <BarChart data={chartData} margin={{
                      top: 10,
                      right: 10,
                      left: 0,
                      bottom: 0
                    }} data-id="element-5668" data-mp="2aq07943m5ak7">
                        
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} data-id="element-5669" data-mp="1n1qpcd9it6yc" />
                        

                        <XAxis dataKey="name" stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} data-id="element-5670" data-mp="1jj9pledstf63" />
                        

                        <YAxis stroke="#6b7280" fontSize={12} tickFormatter={v => `$${v}`} tickLine={false} axisLine={false} data-id="element-5671" data-mp="15j8qo4h3ght9" />
                        

                        <Tooltip cursor={{
                        fill: 'rgba(0,0,0,0.05)'
                      }} contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }} formatter={(value: number) => [formatCurrency(value), '']} data-id="element-5672" data-mp="vrchnydgz7a9" />
                        

                        <Legend wrapperStyle={{
                        paddingTop: '20px'
                      }} data-id="element-5673" data-mp="1nisjffiquol" />
                        

                        {compareServiceIds.length > 0 ? compareServiceIds.map((id, index) => {
                        const svc = services.find(s => s.id === id);
                        if (!svc) return null;
                        return <Bar key={id} dataKey={`${svc.name}_revenue`} name={svc.name} fill={COMPARE_COLORS[index % COMPARE_COLORS.length]} radius={[4, 4, 0, 0]} stackId="a" data-id="element-5674" data-mp="3f0udp8gu3z7u" data-mpi={index} />;
                      }) : <Bar dataKey="totalRevenue" name="Total Ingresos" fill="#10b981" radius={[6, 6, 0, 0]} data-id="element-5675" data-mp="1klykuh8dk9vz" />}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* 3. SUMMARY TABLE */}
            <Card className="overflow-hidden border-none shadow-lg" data-id="element-5676" data-mp="11u5vqxwff8tj">
              <div className="h-1 bg-gradient-to-r from-blue-700 to-blue-500" data-id="element-5677" data-mp="16ujsvkwhoycs" />
              <CardHeader data-id="element-5678" data-mp="1bmbi9s3wsy1c">
                <div className="flex items-center justify-between" data-id="element-5679" data-mp="1bnmm1rxcu5sg">
                  <CardTitle className="flex items-center gap-2" data-id="element-5680" data-mp="e7fsjt8cd87c">
                    <ListIcon className="w-5 h-5 text-gray-600" data-id="element-5681" data-mp="j5r4qam3zusq" />
                    Detalle por Servicio
                  </CardTitle>
                  {canDownloadStatsPdf && summaryData.length > 0 && <button onClick={() => {
                  const detailData = summaryData.map(item => ({
                    name: item.name,
                    count: item.count,
                    totalRevenue: item.revenue,
                    avgPerAppointment: item.count > 0 ? item.revenue / item.count : 0,
                    percentOfTotal: kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0
                  }));
                  exportServiceDetailToPdf(detailData, kpiData.totalRevenue, kpiData.totalCount, {
                    from: dateRange.start,
                    to: dateRange.end
                  });
                }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-5682" data-mp="73g465q1s6hw">
                    
                      <DownloadIcon className="w-4 h-4" data-id="element-5683" data-mp="2hjgmvmrr7a4r" />
                    </button>}
                </div>
              </CardHeader>
              <CardContent className="p-0" data-id="element-5684" data-mp="3a5sy8mgt80sw">
                {/* ── MOBILE CARDS ── */}
                <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-5685" data-mp="3e8655e7am4r9">
                  {summaryData.length === 0 ? <div className="py-10 text-center text-gray-500 text-sm" data-id="element-5686" data-mp="30fq94xct76zu">
                      No hay datos para el periodo seleccionado
                    </div> : paginatedSummary.map((item, idx) => {
                  const percentage = kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0;
                  return <div key={idx} className="px-4 py-3" data-id="element-5687" data-mp="297g9kkiem8o6" data-mpi={idx}>
                          <div className="flex items-start justify-between gap-3 mb-2" data-id="element-5688" data-mp="249ybxq9u7fvh">
                            <div className="flex items-center gap-2 flex-1 min-w-0" data-id="element-5689" data-mp="20a5wqupxubh5">
                              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                          backgroundColor: '#3b82f6'
                        }} data-id="element-5690" data-mp="1vatc8gmjhghq" />
                            

                              <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-5691" data-mp="2j7qn668cpi3v">
                                {item.name}
                              </p>
                            </div>
                            <span className="text-sm font-bold text-blue-600 dark:text-blue-400 flex-shrink-0" data-id="element-5692" data-mp="3bojgpohkm6rw">
                              {formatCurrency(item.revenue)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 ml-4" data-id="element-5693" data-mp="3eak7xh4xi1fp">
                            <span data-id="element-5694" data-mp="3j9wvy57jdejv">{item.count} citas</span>
                            <span data-id="element-5695" data-mp="3o5ucocmpomx6">
                              Prom:{' '}
                              {item.count > 0 ? formatCurrency(item.revenue / item.count) : '-'}
                            </span>
                            <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-5696" data-mp="610rpyv3znuj">
                              {percentage.toFixed(1)}%
                            </span>
                          </div>
                          <div className="mt-2 ml-4 w-full h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-5697" data-mp="frlmfb7nw886">
                            <div className="h-full bg-blue-500 rounded-full transition-all" style={{
                        width: `${percentage}%`
                      }} data-id="element-5698" data-mp="ju04vu7f55yx" />
                          
                          </div>
                        </div>;
                })}
                  {summaryData.length > 0 && <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/80 border-t-2 border-gray-200 dark:border-gray-700" data-id="element-5699" data-mp="2umawb016xghw">
                      <div className="flex justify-between items-center" data-id="element-5700" data-mp="2ps6okek3p4fr">
                        <span className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-5701" data-mp="34h2sbzf4jvak">
                          TOTAL
                        </span>
                        <div className="text-right" data-id="element-5702" data-mp="3dk3trmt1oy7w">
                          <p className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-5703" data-mp="3dhhoxuxjgwtd">
                            {formatCurrency(kpiData.totalRevenue)}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-5704" data-mp="1j5t8tpfkmo7e">
                            {kpiData.totalCount} citas
                          </p>
                        </div>
                      </div>
                    </div>}
                </div>
                {/* ── DESKTOP TABLE ── */}
                <div className="hidden sm:block overflow-x-auto" data-id="element-5705" data-mp="3uv0px3n5xps3">
                  <table className="w-full" data-id="element-5706" data-mp="3h0qc3hcpfg0t">
                    <thead data-id="element-5707" data-mp="3l33f3y02gztw">
                      <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700" data-id="element-5708" data-mp="3l2bj4m2k5033">
                        <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5709" data-mp="372alrgli4asd">
                          Servicio
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5710" data-mp="2y3fee16q0unh">
                          Cantidad
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5711" data-mp="war6vfn1nl1w">
                          Ingresos Totales
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-red-400 uppercase tracking-wider" data-id="element-5712" data-mp="3k9g693mvr66n">
                          Costo
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-green-500 uppercase tracking-wider" data-id="element-5713" data-mp="3aj3mveqsxj23">
                          Ganancia
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5714" data-mp="35i66n7pcqz38">
                          Promedio / Cita
                        </th>
                        <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-5715" data-mp="2wlf021d6c1ue">
                          % del Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-5716" data-mp="pt4iff8yts48">
                      {summaryData.length === 0 ? <tr data-id="element-5717" data-mp="kukvpcmjscat">
                          <td colSpan={7} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-5718" data-mp="tas8p5ecggan">
                          
                            No hay datos para el periodo seleccionado
                          </td>
                        </tr> : paginatedSummary.map((item, idx) => {
                      const percentage = kpiData.totalRevenue > 0 ? item.revenue / kpiData.totalRevenue * 100 : 0;
                      return <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-5719" data-mp="jcm76owg9c6b" data-mpi={idx}>
                            
                              <td className="py-4 px-6 text-sm font-medium text-gray-900 dark:text-white" data-id="element-5720" data-mp="1urrnivpi798e">
                                <div className="flex items-center gap-3" data-id="element-5721" data-mp="1zqm6pyrduzt1">
                                  <div className="w-2.5 h-2.5 rounded-full" style={{
                              backgroundColor: compareServiceIds.length > 0 ? COMPARE_COLORS[compareServiceIds.indexOf(services.find(s => s.name === item.name)?.id || '') % COMPARE_COLORS.length] : '#3b82f6'
                            }} data-id="element-5722" data-mp="1zsp5b76lq19d" />
                                

                                  {item.name}
                                </div>
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-700 dark:text-gray-300 text-right" data-id="element-5723" data-mp="16s2461mqdfqi">
                                {item.count}
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-900 dark:text-white font-semibold text-right" data-id="element-5724" data-mp="1lutsp2err1ay">
                                {formatCurrency(item.revenue)}
                              </td>
                              <td className="py-4 px-6 text-sm text-right" data-id="element-5725" data-mp="1unnb38piigt1">
                                {(item as any).cost > 0 ? <span className="text-red-500 font-medium" data-id="element-5726" data-mp="1ptj1ehov3uo9">
                                    {formatCurrency((item as any).cost * item.count)}
                                  </span> : <span className="text-gray-300" data-id="element-5727" data-mp="2dkesfclhulug">—</span>}
                              </td>
                              <td className="py-4 px-6 text-sm text-right" data-id="element-5728" data-mp="j6x7zygdy53n">
                                {(item as any).cost > 0 ? <span className="text-green-600 dark:text-green-400 font-semibold" data-id="element-5729" data-mp="f6dfj86rbpnu">
                                    {formatCurrency(item.revenue - (item as any).cost * item.count)}
                                  </span> : <span className="text-gray-300" data-id="element-5730" data-mp="2oacd1qkge6za">—</span>}
                              </td>
                              <td className="py-4 px-6 text-sm text-gray-500 dark:text-gray-400 text-right" data-id="element-5731" data-mp="2x9hf36v84ig7">
                                {item.count > 0 ? formatCurrency(item.revenue / item.count) : '-'}
                              </td>
                              <td className="py-4 px-6 text-right" data-id="element-5732" data-mp="3by3s31xdoy49">
                                <div className="flex items-center justify-end gap-2" data-id="element-5733" data-mp="3gwx74ysbcx6j">
                                  <span className="text-xs text-gray-500" data-id="element-5734" data-mp="3guua3usqu3sb">
                                    {percentage.toFixed(1)}%
                                  </span>
                                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-5735" data-mp="19cqazosiiiny">
                                    <div className="h-full bg-indigo-500 rounded-full" style={{
                                width: `${percentage}%`
                              }} data-id="element-5736" data-mp="158a5vpqq3f9s" />
                                  
                                  </div>
                                </div>
                              </td>
                            </tr>;
                    })}
                      {/* Total Row */}
                      {summaryData.length > 0 && <tr className="bg-gray-50 dark:bg-gray-800/80 font-bold border-t-2 border-gray-200 dark:border-gray-700" data-id="element-5737" data-mp="1e9grgd88bg9w">
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white" data-id="element-5738" data-mp="2brqh7dozn386">
                            TOTAL
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white text-right" data-id="element-5739" data-mp="1rfupmnym76gw">
                            {kpiData.totalCount}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900 dark:text-white text-right" data-id="element-5740" data-mp="1hj9poh1gcljx">
                            {formatCurrency(kpiData.totalRevenue)}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-red-500" data-id="element-5741" data-mp="2a75g540fhh89">
                            {formatCurrency(summaryData.reduce((sum, item) => sum + ((item as any).cost || 0) * item.count, 0))}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-green-600 dark:text-green-400" data-id="element-5742" data-mp="2t5hr8vawh16h">
                            {formatCurrency(summaryData.reduce((sum, item) => sum + (item.revenue - ((item as any).cost || 0) * item.count), 0))}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-gray-900 dark:text-white" data-id="element-5743" data-mp="r9ixbaxx15mp">
                            {formatCurrency(kpiData.avgTicket)}
                          </td>
                          <td className="py-4 px-6 text-right text-sm text-gray-900 dark:text-white" data-id="element-5744" data-mp="3uralsfcuf7g5">
                            100%
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination for Summary Table */}
            <Pagination currentPage={summaryPage} totalPages={summaryTotalPages} totalItems={summaryTotalItems} startIndex={summaryStartIndex} endIndex={summaryEndIndex} onPageChange={summaryGoToPage} onNext={summaryNextPage} onPrev={summaryPrevPage} data-id="element-5745" data-mp="1iz3wnk0qao5o" />
            
          </div>
        </TabPanel>
      </Tabs>

      {/* MODALS (Shared for Management) */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedService ? 'Editar Servicio' : 'Nuevo Servicio'} size="md" icon={<BriefcaseIcon className="w-5 h-5" data-id="element-5747" data-mp="6wxwr1ynwlon" />} accentColor="blue" data-id="element-5746" data-mp="3u1x8ktopn38b">
        
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-5748" data-mp="6uuv1ko5d443">
          {/* Service Info */}
          <div data-id="element-5749" data-mp="bph6toy54yop">
            <div className="flex items-center gap-2 mb-4" data-id="element-5750" data-mp="3adeepysrf1na">
              <span className="w-1.5 h-5 rounded-full bg-blue-600" data-id="element-5751" data-mp="3acv6xawtptmz"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-5752" data-mp="3fbfykly0f4zt">
                Información del Servicio
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-5753" data-mp="32tdo5zrel7rh">
              <Input label="Nombre del Servicio" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: Consulta General" data-id="element-5754" data-mp="2xt7obwazceyb" />
              

              <Input label="SKU (Código)" value={formData.sku} onChange={e => setFormData({
              ...formData,
              sku: e.target.value.toUpperCase()
            })} placeholder="Ej: SRV-001" data-id="element-5755" data-mp="2f5cget21tvd4" />
              

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-5756" data-mp="216mjpej55xpa">
                <Input type="number" label="Duración (minutos)" value={formData.duration} onChange={e => setFormData({
                ...formData,
                duration: parseInt(e.target.value) || 0
              })} error={formErrors.duration} min={1} data-id="element-5757" data-mp="3p2j0ii21u7tt" />
                

                <Input type="number" label="Precio (DOP)" value={formData.price} onChange={e => setFormData({
                ...formData,
                price: e.target.value
              })} error={formErrors.price} min={0} step="1" data-id="element-5758" data-mp="368wxqzh2lm9v" />
                

                <Input type="number" label="Costo (DOP)" value={formData.cost} onChange={e => setFormData({
                ...formData,
                cost: e.target.value
              })} error={formErrors.cost} min={0} step="1" placeholder="0" data-id="element-5759" data-mp="2sbgufwu2m8mc" />
                
              </div>
              <Textarea label="Descripción" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} placeholder="Descripción del servicio..." data-id="element-5760" data-mp="2rnri0y7oo2a0" />
              
            </div>
          </div>

          {/* Doctor Assignment */}
          <div data-id="element-5761" data-mp="3af25f5e79wx5">
            <div className="flex items-center gap-2 mb-4" data-id="element-5762" data-mp="3acg0zemlvxj5">
              <span className="w-1.5 h-5 rounded-full bg-indigo-500" data-id="element-5763" data-mp="35gh6raeljlat"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-5764" data-mp="2k361ryr051l">
                Doctores Asignados
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-5765" data-mp="3tta7rk6zq8l2">
              <div className="max-h-48 overflow-y-auto space-y-1" data-id="element-5766" data-mp="7j6b3ceqab9k">
                {activeDoctors.length === 0 ? <p className="text-sm text-gray-500 italic py-2" data-id="element-5767" data-mp="lipbfj1asibb">
                    No hay doctores activos registrados
                  </p> : activeDoctors.map(doctor => {
                const isSelected = formData.doctorIds.includes(doctor.id);
                return <label key={doctor.id} className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-white dark:bg-gray-800 border border-blue-100 dark:border-blue-800/30 shadow-sm' : 'hover:bg-white dark:hover:bg-gray-800 border border-transparent'}`} data-id="element-5768" data-mp="qb8kq3sycrab">
                      
                        <div className="relative" data-id="element-5769" data-mp="1nvi7j8abluz7">
                          <input type="checkbox" checked={isSelected} onChange={() => toggleDoctorSelection(doctor.id)} className="sr-only" data-id="element-5770" data-mp="1nv8k9sgnu1x7" />
                        

                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isSelected ? 'bg-blue-600 border-blue-600 dark:bg-blue-600 dark:border-blue-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'}`} data-id="element-5771" data-mp="1054qsdx6rijx">
                          
                            {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-5772" data-mp="h7le90birhsr">
                            
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-5773" data-mp="2j53w8xgeisov" />
                            
                              </svg>}
                          </div>
                        </div>
                        <span className={`text-sm font-medium ${isSelected ? 'text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-5774" data-mp="36twn4fi62acm">
                        
                          {getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)}{' '}
                          {doctor.name}
                        </span>
                      </label>;
              })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700 modal-actions" data-id="element-5775" data-mp="17cw7qsbz3kn7">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-5776" data-mp="1caxqtd9efjyo">
              
              Cancelar
            </Button>
            <Button type="submit" data-id="element-5777" data-mp="1m20sag68q0yh">
              {selectedService ? 'Guardar Cambios' : 'Crear Servicio'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Servicios" isExporting={isExporting} data-id="element-5778" data-mp="1uzb4c3qvtu4g" />
      

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Servicios a PDF" isExporting={isPdfExporting} data-id="element-5779" data-mp="5mzjn7duq0wo" />
      

      {/* Modal de Pacientes por Servicio */}
      <Modal isOpen={isPatientsModalOpen} onClose={() => setIsPatientsModalOpen(false)} title={`Pacientes: ${selectedServiceForPatients?.name}`} size="lg" icon={<UsersIcon className="w-5 h-5" data-id="element-5781" data-mp="2549eyhenw17q" />} data-id="element-5780" data-mp="1w3l949zf975x">
        
        <div className="space-y-4" data-id="element-5782" data-mp="254j5c63mcady">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5783" data-mp="1hef4g6hteoqn">
            <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-5784" data-mp="1hc2qs5j3b0je">
              Lista de pacientes que han completado citas para este servicio.
            </p>
            <div className="relative w-full sm:w-64" data-id="element-5785" data-mp="1r5t9suwg6irp">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-5786" data-mp="1mdrxxa36yu27" />
              <Input style={{
              paddingLeft: '2.5rem'
            }} placeholder="Buscar paciente..." value={patientsModalSearch} onChange={e => setPatientsModalSearch(e.target.value)} data-id="element-5787" data-mp="ymw1u0rpdcp5" />
              
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-5788" data-mp="3a4hd1j1v8t7n">
            <div className="overflow-x-auto" data-id="element-5789" data-mp="2w9ora2vdugsm">
              <table className="w-full text-sm text-left" data-id="element-5790" data-mp="319t7drsdp9ib">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400" data-id="element-5791" data-mp="3p074vxtqg0ka">
                  <tr data-id="element-5792" data-mp="3t1scb3nhzmn3">
                    <th className="px-4 py-3 font-medium" data-id="element-5793" data-mp="3t2ah4oo8796m">Paciente</th>
                    <th className="px-4 py-3 font-medium" data-id="element-5794" data-mp="3szfv4spkdgb3">Contacto</th>
                    <th className="px-4 py-3 font-medium text-center" data-id="element-5795" data-mp="3f1rc7qf24j8o">
                      Veces Realizado
                    </th>
                    <th className="px-4 py-3 font-medium" data-id="element-5796" data-mp="23muabfxrxzjw">Última Vez</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-5797" data-mp="28mgnxqhilht8">
                  {filteredServicePatients.length === 0 ? <tr data-id="element-5798" data-mp="1ojb2o3tbs8q2">
                      <td colSpan={4} className="px-4 py-8 text-center text-gray-500" data-id="element-5799" data-mp="1olnedz8hpmuo">
                      
                        {patientsModalSearch ? 'No se encontraron pacientes con esa búsqueda.' : 'No hay pacientes registrados para este servicio.'}
                      </td>
                    </tr> : filteredServicePatients.map(item => <tr key={item.patient.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50" data-id="element-5800" data-mp="10tgp1k16mg4r">
                    
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white" data-id="element-5801" data-mp="rttmguwpv4a4">
                          {item.patient.name}
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400" data-id="element-5802" data-mp="2r4l8so6ehp5n">
                          {item.patient.phone || item.patient.email || '-'}
                        </td>
                        <td className="px-4 py-3 text-center" data-id="element-5803" data-mp="2tqu4bkbtfgt1">
                          <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium text-xs" data-id="element-5804" data-mp="2yp5bfnaq7d7k">
                            {item.count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 dark:text-gray-400" data-id="element-5805" data-mp="2at1d3fak0vbh">
                          {item.lastDate}
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Modal>
    </div>;
}